import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Plane, Clock, Luggage, Info } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { readSelectedFlight, type SelectedFlight } from "@/lib/selected-flight";

export const Route = createFileRoute("/flights/details")({
  head: () => ({
    meta: [
      { title: "Detalles del vuelo — Mostrando México" },
      { name: "description", content: "Revisa los detalles del vuelo antes de reservar." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: FlightDetailsPage,
});

const AIRLINE_NAMES: Record<string, string> = {
  AM: "Aeroméxico",
  Y4: "Volaris",
  VB: "VivaAerobus",
  UA: "United",
  AA: "American Airlines",
  DL: "Delta",
  IB: "Iberia",
  AV: "Avianca",
  CM: "Copa Airlines",
  LA: "LATAM",
  AC: "Air Canada",
  AF: "Air France",
  KL: "KLM",
  BA: "British Airways",
  LH: "Lufthansa",
  TK: "Turkish Airlines",
  EK: "Emirates",
  QR: "Qatar Airways",
  B6: "JetBlue",
  WN: "Southwest",
  AS: "Alaska Airlines",
};

function airlineName(iata: string): string {
  return AIRLINE_NAMES[iata] ?? iata;
}

function formatDateLong(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTime(iso?: string): string | null {
  if (!iso || !/T\d{2}:\d{2}/.test(iso)) return null;
  const t = iso.split("T")[1];
  return `${t.slice(0, 2)}:${t.slice(3, 5)}`;
}

function durationLabel(min?: number): string {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

function FlightDetailsPage() {
  const [selected, setSelected] = useState<SelectedFlight | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setSelected(readSelectedFlight());
    setReady(true);
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
          <div className="h-64 animate-pulse rounded-3xl bg-muted" />
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
          <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-card">
            <Plane className="mx-auto h-10 w-10 text-muted-foreground" />
            <h1 className="mt-4 font-display text-2xl font-semibold">
              No hay un vuelo seleccionado
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Vuelve a la búsqueda para elegir un vuelo.
            </p>
            <Link
              to="/flights"
              className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Volver a vuelos
            </Link>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const { offer, passengers } = selected;
  const depTime = formatTime(offer.departAt);
  const retTime = formatTime(offer.returnAt);
  const canBook = typeof offer.deeplink === "string" && offer.deeplink.length > 0;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:py-14">
        <Link
          to="/flights"
          className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Volver a resultados
        </Link>

        <div className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Detalles del vuelo
              </p>
              <h1 className="mt-1 font-display text-3xl font-semibold sm:text-4xl">
                {offer.origin} <span className="text-primary">→</span> {offer.destination}
                {offer.returnDate && (
                  <>
                    {" "}
                    <span className="text-primary">→</span> {offer.origin}
                  </>
                )}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {airlineName(offer.airline)}
                {offer.flightNumber && ` · Vuelo ${offer.airline} ${offer.flightNumber}`}
              </p>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Precio total
              </div>
              <div className="font-display text-3xl font-semibold text-primary sm:text-4xl">
                ${offer.price.toLocaleString("es-MX")}
                <span className="ml-1 text-sm font-medium text-muted-foreground">
                  {offer.currency}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {passengers} {passengers === 1 ? "pasajero" : "pasajeros"}
              </div>
            </div>
          </div>

          {/* Outbound */}
          <Leg
            title="Ida"
            date={offer.departDate}
            time={depTime}
            from={offer.origin}
            to={offer.destination}
            transfers={offer.transfers}
            durationMin={offer.durationToMinutes ?? offer.durationMinutes}
          />

          {offer.returnDate && (
            <Leg
              title="Regreso"
              date={offer.returnDate}
              time={retTime}
              from={offer.destination}
              to={offer.origin}
              transfers={offer.returnTransfers ?? offer.transfers}
              durationMin={offer.durationBackMinutes}
            />
          )}

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <InfoBlock icon={Luggage} title="Equipaje">
              El equipaje incluido depende de la aerolínea y la tarifa. El precio mostrado es
              referencial y se confirma con la aerolínea o agencia socia antes de pagar.
            </InfoBlock>
            <InfoBlock icon={Info} title="Condiciones de la tarifa">
              Cambios y cancelaciones sujetos a las políticas de {airlineName(offer.airline)}. Las
              condiciones completas se muestran antes de finalizar la compra.
            </InfoBlock>
          </div>

          <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              Al continuar serás dirigido al proveedor de la aerolínea para completar tu compra de
              forma segura.
            </p>
            <a
              href={canBook ? offer.deeplink : "#"}
              target="_blank"
              rel="noopener noreferrer sponsored"
              aria-disabled={!canBook}
              onClick={(e) => {
                if (!canBook) e.preventDefault();
              }}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-glow transition-transform hover:scale-[1.02] aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
            >
              Reservar ahora <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Leg({
  title,
  date,
  time,
  from,
  to,
  transfers,
  durationMin,
}: {
  title: string;
  date: string;
  time: string | null;
  from: string;
  to: string;
  transfers: number;
  durationMin?: number;
}) {
  return (
    <div className="mt-6 rounded-2xl border border-border bg-background p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
          {title}
        </div>
        <div className="text-xs text-muted-foreground">{formatDateLong(date)}</div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <div>
          <div className="font-display text-2xl font-semibold">{time ?? "—:—"}</div>
          <div className="text-xs font-semibold text-muted-foreground">{from}</div>
        </div>
        <div className="flex flex-1 flex-col items-center">
          <span className="text-[11px] font-medium text-muted-foreground">
            <Clock className="mr-1 inline h-3 w-3" />
            {durationLabel(durationMin)}
          </span>
          <div className="relative my-1 h-px w-full min-w-[60px] bg-border">
            <Plane className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-90 text-primary" />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {transfers === 0 ? "Directo" : `${transfers} escala${transfers > 1 ? "s" : ""}`}
          </span>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-semibold">—:—</div>
          <div className="text-xs font-semibold text-muted-foreground">{to}</div>
        </div>
      </div>
    </div>
  );
}

function InfoBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Plane;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <div className="mb-1 inline-flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}
